import React from 'react';

import ReactDOM from 'react-dom';
import { LatLng, Location as BringgLocation } from '@bringg/types';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import { ExtendedMarkerData, ExtendedPolylineData, IdType, TooltipElement, TriggerType } from '../../types';
import { isLatLngValid } from '../../utils';
import { UPDATE_DRIVER_LOCATION_THROTTLE_MS } from '../../bringg-map-route-player/consts';
import { getDataForMarker, setDataForMarker } from './markers-to-marker-data';
import IconMarker from './icon-marker';

import style from './google-map-markers.module.scss';

export const MARKER_REPOSITION_ANIMATION_DISTANCE_LIMIT = 0.01;
export const MARKER_REPOSITION_ANIMATION_DELTA = 50;
export const MARKER_REPOSITION_ANIMATION_DELAY = UPDATE_DRIVER_LOCATION_THROTTLE_MS / MARKER_REPOSITION_ANIMATION_DELTA;
export const DEFAULT_MAX_ZOOM = 19;
export const DEFAULT_MIN_ZOOM = 4;
const DEFAULT_LABEL_COLOR = 'white';
const DEFAULT_CUSTOM_MARKER_ZINDEX = 99;
export const SELECTED_ICON_SCALE = 2;

export const addGoogleMarker = ({
	map,
	markerData: initialMarkerData,
	infoWindow,
	onSelectMarker,
	selectedMarkers,
	shouldOpenTooltipOnCreation
}: {
	map: google.maps.Map;
	markerData: ExtendedMarkerData;
	infoWindow: google.maps.InfoWindow;
	onSelectMarker?: (isCtrlClick: boolean, markerId: IdType) => void;
	selectedMarkers?: IdType[];
	hideAnimation?: boolean;
	shouldOpenTooltipOnCreation?: boolean;
}) => {
	if (!map || !isLatLngValid(initialMarkerData?.location)) return;
	const zIndex = initialMarkerData.zIndex || DEFAULT_CUSTOM_MARKER_ZINDEX;

	const getPinMarker = (isSelected: boolean) => {
		const container = document.createElement('div');
		ReactDOM.render(
			<div
				style={{
					['--pin-color' as any]: initialMarkerData.color,
					['--text-color' as any]: initialMarkerData.textColor || DEFAULT_LABEL_COLOR,
					['--opacity-scale' as any]: initialMarkerData.opacity || 1,
					zIndex: zIndex
				}}
				data-test-id={`pin-marker-${initialMarkerData.id}`}
				className={classNames(style.marker, {
					[style.selected]: isSelected
				})}
				title={initialMarkerData.title}
			>
				{initialMarkerData.text}
			</div>,

			container
		);

		return container;
	};

	const getIconMarker = (initialMarkerData: ExtendedMarkerData) => {
		const container = document.createElement('div');

		ReactDOM.render(
			<IconMarker
				title={initialMarkerData.title || ''}
				icon={<BringgIcon iconName={initialMarkerData.icon as BringgFontIcons} />}
				color={initialMarkerData.color}
			/>,

			container
		);

		return container;
	};

	const getMarker = () => {
		if (initialMarkerData.icon) {
			return getIconMarker(initialMarkerData);
		} else if (initialMarkerData.color) {
			const isSelected =
				selectedMarkers && selectedMarkers.length >= 1 && selectedMarkers.includes(initialMarkerData.id);
			return getPinMarker(!!isSelected);
		}
	};

	const marker = new google.maps.marker.AdvancedMarkerElement({
		map,
		position: new google.maps.LatLng(initialMarkerData.location.lat, initialMarkerData.location.lng),
		content: getMarker(),
		gmpDraggable: initialMarkerData.draggable || false
	});

	let markerTriggerListener;
	if (initialMarkerData.tooltip) {
		const trigger = initialMarkerData.tooltipOptions?.trigger || TriggerType.MOUSEOVER;

		if (shouldOpenTooltipOnCreation) {
			if (initialMarkerData.tooltip) {
				// Trigger lazy rendering if the tooltip has a renderTooltip function
				(initialMarkerData.tooltip as TooltipElement).renderTooltip?.();
				infoWindow.setContent(initialMarkerData.tooltip);
			}
			infoWindow.open(map, marker);
		}

		markerTriggerListener = google.maps.event.addListener(marker, trigger, (event: any) => {
			const markerData = getDataForMarker(marker);
			if (!markerData) {
				return;
			}

			const ctrlKey = event.domEvent.metaKey || event.domEvent.ctrlKey;
			onSelectMarker?.(ctrlKey, markerData.id);

			if (initialMarkerData.tooltip) {
				// Trigger lazy rendering if the tooltip has a renderTooltip function
				(markerData.tooltip as TooltipElement).renderTooltip?.();
				infoWindow.setContent(markerData.tooltip);
			}
			if (ctrlKey) {
				infoWindow.close();
			} else {
				infoWindow.open(map, marker);
			}
		});
	}

	if (markerTriggerListener) {
		initialMarkerData.markerTriggerListener = markerTriggerListener;
	}

	setDataForMarker(marker, initialMarkerData);

	return marker;
};

export const addGooglePolyline = ({
	map,
	polylineData
}: {
	map: google.maps.Map;
	polylineData: ExtendedPolylineData;
}) => {
	const polylinePath: google.maps.LatLng[] = polylineData.path.flatMap(p => {
		if (!isLatLngValid(p)) return [];

		const { lat, lng } = p;
		return [new google.maps.LatLng(lat, lng)];
	});

	const polylineOptions: google.maps.PolylineOptions = {
		strokeColor: polylineData?.color ?? '#3d8deb',
		strokeOpacity: polylineData?.strokeOpacity ?? 1.0,
		strokeWeight: polylineData?.strokeWeight ?? 5,
		path: polylinePath,
		visible: true
	};

	const generatedPolyline = new google.maps.Polyline(polylineOptions);

	if (polylineData.onMouseover) {
		google.maps.event.addListener(generatedPolyline, 'mouseover', () => {
			if (polylineData.onMouseover) {
				polylineData.onMouseover(generatedPolyline);
			}
		});
	}
	if (polylineData.onMouseout) {
		google.maps.event.addListener(generatedPolyline, 'mouseout', () => {
			if (polylineData.onMouseout) {
				polylineData.onMouseout(generatedPolyline);
			}
		});
	}

	generatedPolyline.setMap(map);
	return generatedPolyline;
};

export const addLocationToBounds = (location: LatLng, bounds: google.maps.LatLngBounds) => {
	const { lat, lng } = location;
	if (isNaN(lat) || isNaN(lng)) return;

	bounds.extend(new google.maps.LatLng(lat, lng));
};

export const removeEntityFromMap = (entity: google.maps.marker.AdvancedMarkerElement) => {
	entity.map = null;
};

export const setMapZoom = (map: google.maps.Map, zoom: number) => {
	// @ts-ignore
	map.systemZoomChange = true;
	map.setZoom(zoom);
};

export const setMapCenter = (map: google.maps.Map, center: google.maps.LatLng | BringgLocation) => {
	// @ts-ignore
	map.systemZoomChange = true;
	map.setCenter(center);
};

export const fitMapBound = (map: google.maps.Map, bounds: google.maps.LatLngBounds, padding?: number) => {
	// @ts-ignore
	map.systemZoomChange = true;
	google.maps.event.addListenerOnce(map, 'bounds_changed', function () {
		// @ts-ignore
		map.systemZoomChange = true;
	});
	map.fitBounds(bounds, padding);
};

export const panMap = (map: google.maps.Map, pin: google.maps.LatLng) => {
	// @ts-ignore
	map.systemZoomChange = true;
	map.panTo(pin);
};

export const shouldDisplayTooltipForSelectedMarker = (
	selectedMarkers: IdType[] | undefined,
	markerId: IdType
): boolean => {
	return Boolean(selectedMarkers && selectedMarkers.length === 1 && selectedMarkers[0] === markerId);
};
